import { ArrowDownIcon, ArrowUpIcon, ErrorIcon, CloseIcon } from '@/components/icons'
import { useTranslation } from '@/hooks/useTranslation'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SxProps,
  Typography,
  IconButton
} from '@mui/material'
import { formHelperTextClasses } from '@mui/material/FormHelperText'
import { Controller, UseFormReturn, useController } from 'react-hook-form'

import { ProgramMode } from '@/types/program'
import { useState } from 'react'
import classes from './style.module.scss'

interface SelectFieldProps {
  label?: string
  name: string
  optionList: { value: any; label: string }[]
  form: UseFormReturn<any, any>
  className?: string
  sx?: SxProps
  icon?: any
  placeholder?: string
  isRequired?: boolean
  heightScroll?: string
  showClearButton?: boolean
}

const SelectField = ({
  label,
  name,
  form,
  optionList,
  sx,
  icon,
  className,
  placeholder,
  isRequired,
  heightScroll,
  showClearButton = false,
}: SelectFieldProps) => {
  const { t } = useTranslation()
  const { control } = form || { control: null }

  const {
    formState: { errors },
  } = control
    ? // eslint-disable-next-line
      useController({ name, control })
    : {
        formState: { errors: undefined },
      }

  const [open, setOpen] = useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleClear = (fieldOnChange: (value: any) => void) => {
    fieldOnChange('') // Clear the field value
  }

  // Check if field has value to show clear button
  const hasValue = (value: any) => {
    return   value !== undefined && value !== null && value !== ''
  }

  return (
    <>
      <Controller
        name={name}
        control={form?.control}
        render={({ field }) => (
          <FormControl
            variant="standard"
            sx={{
              height: '50px',
              padding: 0,
              backgroundColor: 'background.overrideDefault',
              borderRadius: '8px',
              overflow: 'hidden',
              ...sx,
            }}
          >
            <InputLabel
              sx={{
                zIndex: '2',
                left: '16px',
                top: '-4px',
                fontWeight: 700,
                color: !!errors?.[name] ? 'primary.contrastText' : 'text.main',
                '&.MuiInputLabel-shrink': {
                  top: '8px',
                },
                '& .MuiFormLabel-root-MuiInputLabel-root.Mui-focused': {
                  color: !!errors?.[name] ? 'primary.contrastText' : 'blue',
                },
              }}
            >
              {label}
            </InputLabel>
            <Select
              className={className}
              required={isRequired}
              open={open}
              onClose={handleClose}
              onOpen={handleOpen}
              IconComponent={() =>
                open ? (
                  <ArrowUpIcon />
                ) : (
                  <ArrowDownIcon
                    onClick={handleOpen}
                    sx={{ cursor: 'pointer' }}
                  />
                )
              }
              MenuProps={{
                sx: {
                  '&& .Mui-selected': {
                    backgroundColor: 'background.dialog',
                    borderBottom: '1px solid #D7D7D7',
                    color: !!errors?.[name] ? 'text.main' : 'text.main',
                  },
                  '&& .MuiPaper-root': {
                    padding: '0px',
                    mt: '6px',
                    ml: '9px',
                  },
                  '&& .MuiList-root': {
                    paddingTop: '0px',
                    paddingBottom: '0px',
                    maxHeight: heightScroll,
                    paddingRight: '0px',
                    overflowY: 'auto',
                    borderRadius: '5px',
                    '&:hover': {
                      color: 'red',
                    },
                    '&::-webkit-scrollbar': {
                      width: '5px',
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'background.overrideDefault',
                      borderRadius: '5px',
                    },
                    minWidth: '100px',
                  },
                },
              }}
              sx={{
                height: '100% !important',
                fontSize: '12px',
                padding: '0 16px',
                marginTop: '0 !important',
                color: !!errors?.[name] ? 'error.main' : 'text.main',
                borderColor: !!errors?.[name] ? 'error.main' : '#F1F1F1',
                backgroundColor: !!errors?.[name]
                  ? 'error.main'
                  : 'background.overrideDefault',
                '& .MuiSelect-select': {
                  height: '100% !important',
                  display: 'inline-flex',
                  alignItems: 'flex-end',
                  padding: '8px 0',
                  boxSizing: 'border-box',
                  marginTop: '6px',
                },

                ...(isRequired && {
                  [`& .MuiInputLabel-root::after`]: {
                    content: '"*"',
                    color: '#ED2121',
                    fontFamily: 'inherit',
                    verticalAlign: 'middle',
                    fontSize: '14px',
                    marginTop: '6px',
                  },
                }),

                '& .MuiInput-input:focus': {
                  backgroundColor: !!errors?.[name]
                    ? 'error.main'
                    : 'background.overrideDefault',
                  marginTop: '6px',
                },
                '& .MuiSvgIcon-root': {
                  right: '-5px !important',
                  top: '0px !important',
                  position: 'relative',
                  color: !!errors?.[name]
                    ? 'primary.contrastText'
                    : 'text.main',
                },

                '&.Mui-focused input.placeholder': {
                  display: 'inline-block',
                  marginTop: '6px',
                },
                '& .placeholder': {
                  fontSize: '12px',
                  display: 'none',
                  border: 'none',
                  background: 'transparent',
                  marginTop: '6px',
                },
                ...sx,
              }}
              disableUnderline
              displayEmpty={true}
              renderValue={(value) => {
                return value?.toString().length ? (
                  Array.isArray(value) ? (
                    value.join(', ')
                  ) : (
                    optionList.find((item) => item.value === value)?.label
                  )
                ) : (
                  <input className="placeholder" placeholder={placeholder} />
                )
              }}
              {...field}
              value={field.value ?? ''}
            >
              {optionList.length === 0 ? (
                <Stack sx={{ ...sx, padding: '10px', textAlign: 'center' }}>
                  <Typography>{t('formsSelectNoOptions')}</Typography>
                </Stack>
              ) : (
                optionList.map((item: any, key: any) => (
                  <MenuItem
                    value={item.value}
                    key={key}
                    sx={{
                      ...sx,
                      opacity: item.value === ProgramMode.RunCisgenET ? 0.2 : 1, // Adjust the opacity as needed
                      pointerEvents:
                        item.value === ProgramMode.RunCisgenET
                          ? 'none'
                          : 'auto',
                      userSelect: 'none',
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))
              )}
            </Select>
            {showClearButton && hasValue(field.value) && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation() // Prevent opening select dropdown
                  handleClear(field.onChange)
                }}
                sx={{
                  position: 'absolute',
                  right: '30px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 3,
                  padding: '4px',
                  color: "text.main",
                }}
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </FormControl>
        )}
      />
      {errors?.[name]?.message && (
        <Stack className={classes.textError}>
          <ErrorIcon />
          <FormHelperText
            id="component-helper-text"
            error
            sx={{
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '115%',
              flex: 'none',
              letterSpacing: '0.045em',
              [`&.${formHelperTextClasses.error}`]: {
                color: '#FF3030',
                marginLeft: '9px',
                marginTop: '2px',
              },
            }}
          >
            {errors?.[name]?.message as string}
          </FormHelperText>
        </Stack>
      )}
    </>
  )
}

export default SelectField
